import { useRef } from 'react'
import { useRealtimeSubscription, useSetState } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as artworkPageActions from '@redux/modules/artworkPage'
import { hydrateArtwork } from '@redux/modules/artwork'

import type { AppDispatch } from '@redux/store'
import type { ArtworkPageModel } from '@domain-group/realhub-backend-api-types'
import type { EntitiesState } from '@redux/entities'

type DefaultState = {
  attachmentType: string,
  selectedArtworkPageId: number | undefined,
}

const defaultState: DefaultState = {
  attachmentType: 'preview_image',
  selectedArtworkPageId: undefined,
}

type SetState = (state: Partial<DefaultState>) => void

const getBuildOptions = (selectedArtworkPageId?: number) => {
  if (!selectedArtworkPageId) return {}

  return {
    broadcast_build_completion: true,
    broadcast_channel: 'artwork-previews-api',
    broadcast_event: `artworkPage.${selectedArtworkPageId}.build.complete`,
  }
}

type FindArtworkPageAttachmentTypeParams = {
  artworkPage: ArtworkPageModel,
  attachmentType: DefaultState['attachmentType'],
  attachments: EntitiesState['attachments'],
}

const findArtworkPageAttachmentType = (params: FindArtworkPageAttachmentTypeParams) => {
  const { artworkPage, attachments, attachmentType } = params

  const array = Object.values(attachments)
  const match = array.find((attachment) => {
    const { attachable_type, attachable_id, attachment_type } = attachment

    return (
      attachable_type === 'ArtworkPage'
      && attachable_id === artworkPage.id
      && attachment_type === attachmentType
    )
  })

  return match || {}
}

type BuildPreviewParams = {
  artworkBuildOptions?: object,
  artworkPage: ArtworkPageModel,
  buildingRef: {
    current: boolean,
  },
  dispatch: AppDispatch,
  queryParams?: object,
  setState: SetState,
}

const buildPreview = (params: BuildPreviewParams) => {
  const {
    artworkBuildOptions = {},
    artworkPage,
    buildingRef,
    dispatch,
    queryParams = {},
    setState,
  } = params

  const { buildArtworkPagePreview } = artworkPageActions

  buildingRef.current = true

  setState({
    selectedArtworkPageId: artworkPage.id,
  })

  const mergedOptions = {
    ...getBuildOptions(artworkPage.id),
    ...queryParams,
  }

  return dispatch(buildArtworkPagePreview(artworkPage.id, artworkBuildOptions, mergedOptions))
}

export type BuildCompleteResult = {
  filters: {
    artwork_page_id: number,
  },
  options: {
    attachment_type: string,
  },
  payload: ArtworkPageModel,
}

type BuildCompleteParams = {
  buildingRef: {
    current: boolean,
  },
  dispatch: AppDispatch, lt: BuildCompleteResult,
  result: {
    filters: {
      artwork_page_id: number,
    },
    options: {
      attachment_type: string,
    },
    payload: ArtworkPageModel,
  },
  selectedArtworkPageId: number,
  setState: SetState,
}

const buildComplete = async (params: BuildCompleteParams) => {
  const {
    buildingRef,
    dispatch,
    result,
    selectedArtworkPageId,
    setState,
  } = params

  const { hydrateArtworkPage } = artworkPageActions

  const {
    filters: { artwork_page_id },
    options: { attachment_type },
    payload,
  } = result

  if (artwork_page_id === selectedArtworkPageId){
    await dispatch(hydrateArtworkPage(payload))
    // @ts-expect-error TS2353
    await dispatch(hydrateArtwork({ id: payload.artwork_id, preview: payload.previews }))

    buildingRef.current = false

    setState({
      attachmentType: attachment_type || '',
    })
  }
}

export type UseArtworkPagePreviewOptions = {
  ablyPublicKey: string,
  artworkBuildOptions?: object,
}

function useArtworkPagePreview(options: UseArtworkPagePreviewOptions) {
  const { ablyPublicKey, artworkBuildOptions } = options

  const [state, setState] = useSetState(defaultState)
  const { attachmentType, selectedArtworkPageId } = state

  const buildingRef = useRef(false)

  const dispatch = useDispatch()

  const entitiesRef = useRef({})
  entitiesRef.current = useSelector(reduxState => reduxState.entities)
  // @ts-expect-error TS2339
  const { artworkPages, attachments } = entitiesRef.current

  const buildOptions = getBuildOptions(selectedArtworkPageId)

  useRealtimeSubscription({
    apiKey: ablyPublicKey,
    callbacks: {
      success: (result: BuildCompleteResult) => buildComplete({
        buildingRef,
        dispatch,
        result,
        // @ts-expect-error TS2322
        selectedArtworkPageId,
        setState,
      }),
    },
    channelName: buildOptions.broadcast_channel,
    eventName: buildOptions.broadcast_event,
  }, [selectedArtworkPageId])

  const selectedArtworkPage = artworkPages[selectedArtworkPageId] || {}
  const attachment = findArtworkPageAttachmentType({
    artworkPage: selectedArtworkPage,
    attachmentType,
    attachments,
  })

  return {
    attachment,
    buildingRef,
    callbacks: {
      buildPreview: (
        payload: {
          artworkPage: ArtworkPageModel,
          queryParams?: object,
        },
      ) => {
        const { artworkPage, queryParams } = payload

        return buildPreview({
          artworkPage,
          artworkBuildOptions,
          buildingRef,
          queryParams,
          setState,
          dispatch,
        })
      },
      setState,
    },
    ...state,
  }
}

export type UseArtworkPagePreviewPayload = ReturnType<typeof useArtworkPagePreview>

export default useArtworkPagePreview
